<template>
    <StepModal
        ref="stepModal"
        :title="$t('labels.createIntegrationProducts')"
        :steps="steps"
        @submit="submit"
        @on-hide="reset"
    />
    <CreateIntegrationProductsResultModal ref="resultModal" />
</template>
<script>
import StepModal from '../../../../components/new/modal/StepModal.vue';
import ProductsSelectionStep from './ProductsSelectionStep.vue';
import FieldsSelectionComponent from './FieldsSelectionComponent.vue';
import CategoriesMatchStep from './CategoriesMatchStep.vue';
import CreateIntegrationProductsResultModal from './CreateIntegrationProductsResultModal.vue';

import { httpClient } from '../../../../services/HttpClient';
import MessageNotificator from '../../../../services/MessageNotificator';
import { CategoryDictionaryEnum } from '../../../enums/CategoryDictionaryEnum';
import { IntegrationServiceEnum } from '../../../enums/IntegrationServiceEnum';

export default {
    name: "CreateIntegrationProductsModal",
    components: {
        StepModal, ProductsSelectionStep, FieldsSelectionComponent, CategoriesMatchStep,
        CreateIntegrationProductsResultModal
    },
    props: {
        targetIntegrationService: {
            type: String,
            required: true
        },
        targetIntegrationUuid: {
            type: String,
            required: true
        },
        targetIntegrationName: {
            type: String,
            required: true
        },
        integrationSourceUuid: {
            type: String,
            required: true
        }
    },
    emits: ['updateTable'],
    data() {
        return {
            products: [],
            totalProductsCount: 0,
            tableFilters: {},
            selectionMode: null,
            categoriesMatch: [],
            fieldsWhoCanBeMapped: [
                { key: 'source_id',         label: this.$t('labels.source_id'),         selected: false },
                { key: 'second_source_id',  label: this.$t('labels.second_source_id'),  selected: false },
                { key: 'sku',               label: this.$t('labels.sku'),               selected: true },
                { key: 'names',             label: this.$t('labels.names'),             selected: true },
                { key: 'descriptions',      label: this.$t('labels.descriptions'),      selected: true },
                { key: 'seos',              label: this.$t('labels.seos'),              selected: true },
                { key: 'images',            label: this.$t('labels.images'),            selected: true },
                { key: 'group_key',         label: this.$t('labels.group_key'),         selected: true },
                { key: 'manufacturer_name', label: this.$t('labels.manufacturer_name'), selected: true },
                { key: 'price',             label: this.$t('labels.price'),             selected: true },
                { key: 'sale_price',        label: this.$t('labels.sale_price'),        selected: true },
                { key: 'balance',           label: this.$t('labels.balance'),           selected: true },
            ],
        };
    },
    methods: {
        show(products, totalProductsCount = 0, tableFilters = {}) {
            if (products.length === 0 && totalProductsCount === 0) {
                new MessageNotificator().warning(this.$t('alerts.productsAlreadyHaveTarget'));
                return;
            }

            this.products = products;
            this.totalProductsCount = totalProductsCount;
            this.tableFilters = tableFilters;
            this.selectionMode = products.length > 0 ? { value: 'selected' } : { value: 'all' };

            this.$refs.stepModal.show();
        },
        reset() {
            this.$refs.stepModal.reset();
            this.products = [];
            this.totalProductsCount = 0;
            this.tableFilters = {};
            this.selectionMode = null;
            this.categoriesMatch = [];
            this.fieldsWhoCanBeMapped.forEach(field => {
                field.selected = !['source_id', 'second_source_id'].includes(field.key);
            });
        },
        async findCategoryInIntegrationCatalog(targetIntegrationUuid, categorySourceId) {
            let category = null;

            await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${targetIntegrationUuid}/categories`, {
                source_id: categorySourceId,
                limit: 1
            }).then((response) => {
                if (response.data.data.length > 0) {
                    category = response.data.data[0];
                }
            })

            return category;
        },
        async createIntegrationCategory(integrationUuid, sourceId, nameUk, nameRu, parentSourceId) {
            let createdCategory = null;

            const requestBody = {
                source_id: sourceId
            };

            if (nameUk && nameUk.length > 0) {
                requestBody.names = requestBody.names || {};
                requestBody.names.uk = nameUk;
            }

            if (nameRu && nameRu.length > 0) {
                requestBody.names = requestBody.names || {};
                requestBody.names.ru = nameRu;
            }

            if (this.targetIntegrationService === IntegrationServiceEnum.KASTA) {
                requestBody.parent_source_id = parentSourceId?.toString();
            }

            await httpClient.post(`/projects/${this.$route.params.projectUuid}/pim/channels/${integrationUuid}/categories`, requestBody).then((response) => {
                createdCategory = response.data.data;
            });

            return createdCategory;
        },
        async resolveTargetCategoryUuid(match) {
            if (match.directory === CategoryDictionaryEnum.LOCAL) {
                return match.targetIntegrationCategory?.uuid ?? null;
            }

            if (match.directory === CategoryDictionaryEnum.COMMON && match.commonTargetIntegrationCategory) {
                const commonCategory = match.commonTargetIntegrationCategory;
                const categorySourceId = commonCategory.source_id.toString();
                const category = await this.findCategoryInIntegrationCatalog(this.targetIntegrationUuid, categorySourceId);

                if (category) {
                    return category.uuid;
                }

                const createdCategory = await this.createIntegrationCategory(
                    this.targetIntegrationUuid,
                    categorySourceId,
                    commonCategory.name_uk,
                    commonCategory.name_ru,
                    commonCategory.parent_source_id
                );

                return createdCategory?.uuid ?? null;
            }

            return null;
        },
        async resolveCategoriesMatch() {
            const resolved = [];

            for (const match of this.categoriesMatch) {
                const targetCategoryUuid = await this.resolveTargetCategoryUuid(match);

                if (targetCategoryUuid) {
                    resolved.push({
                        source_category_uuid: match.sourceCategoryUuid,
                        target_category_uuid: targetCategoryUuid,
                    });
                }
            }

            return resolved;
        },
        async submit() {
            try {
                const categoriesMatch = await this.resolveCategoriesMatch();
                const fields = this.fieldsWhoCanBeMapped.filter(field => field.selected).map(field => field.key);

                const payload = {
                    categories_match: categoriesMatch,
                    fields
                };

                if (this.selectionMode.value === 'selected') {
                    payload.uuids = this.products.map(product => product.source.uuid);
                } else {
                    Object.assign(payload, this.tableFilters);
                }

                const response = await httpClient.post(
                    `/integrations/${this.integrationSourceUuid}/compare/${this.targetIntegrationUuid}/products/create`,
                    payload
                );

                this.$refs.stepModal.hide();
                this.$emit('updateTable');

                if (response.data.skipped?.length > 0) {
                    this.$nextTick(() => this.$refs.resultModal.show(response.data));
                }
            } catch (e) {
                this.$refs.stepModal.isLoading = false;
            }
        },
    },
    computed: {
        steps() {
            return [
                {
                    component: ProductsSelectionStep,
                    description: this.$t('labels.create_integration_products_select_step_description'),
                    props: {
                        selectedProductsCount: this.products.length,
                        totalProductsCount: this.totalProductsCount,
                        modelValue: this.selectionMode,
                        'onUpdate:modelValue': (val) => { this.selectionMode = val; },
                    },
                },
                {
                    component: CategoriesMatchStep,
                    description: this.$t('labels.create_integration_products_category_step_description'),
                    size: 'lg',
                    props: {
                        targetIntegrationService: this.targetIntegrationService,
                        targetIntegrationUuid: this.targetIntegrationUuid,
                        targetIntegrationName: this.targetIntegrationName,
                        integrationSourceUuid: this.integrationSourceUuid,
                        selectionMode: this.selectionMode,
                        products: this.products,
                        tableFilters: this.tableFilters,
                        'onUpdate:categoriesMatch': (val) => { this.categoriesMatch = val; },
                    },
                },
                {
                    component: FieldsSelectionComponent,
                    description: this.$t('labels.create_integration_products_fields_step_description'),
                    props: {
                        fieldsWhoCanBeMapped: this.fieldsWhoCanBeMapped,
                    },
                },
            ];
        }
    }
}
</script>
